import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { DororaidIcon } from '../../shared/ui/DororaidIcon.jsx';
import {
  loadStickerFavorites,
  loadStickers,
  readCachedSticker,
  readFavoriteIds,
  rememberStickerForChat,
  setStickerFavorite,
  stickerAssetUrl,
} from './stickerApi.js';
import './stickers.css';

const STICKER_LIST_TTL_MS = 5 * 60_000;

// 영상 콘은 재생 버튼을 눌러야 움직였다(controls). 콘은 GIF처럼 알아서 움직여야 하므로 소리 없이
// 반복 재생한다. 다만 autoplay 속성만 주면 스크롤로 지나간 콘까지 계속 돌아가므로, 화면에 들어올
// 때 play / 나갈 때 pause 한다. 채팅은 메타데이터와 포스터를 먼저 받아 빈칸을 막는다.
function StickerVideo({ src, poster, preview = false, onError }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  // 지금 화면 안인지. 탭이 다시 보일 때 '원래 보이던 것만' 다시 틀기 위해 들고 있는다.
  const visibleRef = useRef(false);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    // muted 를 속성으로만 두면 브라우저가 자동재생을 막는 경우가 있어 프로퍼티로도 세운다.
    video.muted = true;
    video.defaultMuted = true;
    const play = () => {
      if (document.visibilityState === 'hidden') return;
      video.play?.().catch(() => { if (visibleRef.current) setPlaybackBlocked(true); });
    };
    if (typeof IntersectionObserver !== 'function') {
      visibleRef.current = true;
      play();
      return undefined;
    }
    // rootMargin 을 주면 그만큼 화면 밖에서도 계속 돈다. 전에는 120px 였다 - 스크롤로 올려 보낸
    // 콘이 눈에 안 보이는 채로 계속 재생돼 배터리만 먹었다. 화면에서 벗어나면 바로 멈춘다.
    // 들어올 때 늦게 시작하는 대신 poster 가 먼저 보이므로 빈 칸이 보이지는 않는다.
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        visibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting) play();
        else video.pause?.();
      }
    }, { rootMargin: '0px' });
    observer.observe(video);

    // 탭을 가리거나 앱을 내리면 화면 안에 있던 콘은 그대로 돌아간다. 관찰자는 그때 안 울린다.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') video.pause?.();
      else if (visibleRef.current) play();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [src]);

  // Safari PWA에서는 play()가 지연·차단될 수 있다. 별도 이미지를 먼저 그려
  // 영상의 첫 프레임과 IntersectionObserver를 기다리는 동안 빈 콘이 되지 않게 한다.
  return <span className="doro-sticker-media doro-sticker-video">
    <video ref={videoRef} className="doro-sticker-video__player" src={src} poster={poster || undefined}
      width={128} height={128} autoPlay muted loop playsInline preload={preview ? 'none' : 'metadata'} aria-label="동영상 콘"
      onPlaying={() => { setPlaying(true); setPlaybackBlocked(false); }}
      onPause={() => setPlaying(false)} onError={() => {
        if (!poster) { onError?.(); return; }
        setPlaying(false);
        setPlaybackBlocked(true);
      }} />
    {!playing && poster ? <img className="doro-sticker-video__poster" src={poster} alt=""
      width={128} height={128} loading={preview ? 'lazy' : 'eager'} aria-hidden="true" onError={onError} /> : null}
    {!preview && playbackBlocked && !playing ? <button type="button" className="doro-sticker-video__play"
      aria-label="콘 영상 재생" onClick={() => {
        const video = videoRef.current;
        if (video) {
          video.muted = true;
          if (video.error) video.load();
          video.play()?.catch(() => setPlaybackBlocked(true));
        }
      }}>재생</button> : null}
  </span>;
}

function StickerMedia({ sticker, preview = false }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span>콘을 표시할 수 없습니다.</span>;
  const mediaUrl = stickerAssetUrl(sticker.mediaUrl);
  const posterUrl = stickerAssetUrl(sticker.posterUrl);
  if (sticker.mediaType === 'video' && !(preview && posterUrl)) {
    return <StickerVideo src={mediaUrl} poster={posterUrl} preview={preview} onError={() => setFailed(true)} />;
  }
  return <img className="doro-sticker-media" src={preview && posterUrl ? posterUrl : mediaUrl}
    alt="콘" width={128} height={128} loading={preview ? 'lazy' : 'eager'} onError={() => setFailed(true)} />;
}

export function ChatSticker({ stickerId, stickerIds, scope = 'control' }) {
  const ids = Array.isArray(stickerIds) && stickerIds.length ? stickerIds.slice(0, 2) : [stickerId];
  if (ids.length === 1) return <SingleChatSticker stickerId={ids[0]} scope={scope} />;
  return <div className="doro-sticker-pair">{ids.map((id, index) => <SingleChatSticker key={`${index}:${id}`} stickerId={id} scope={scope} />)}</div>;
}

function SingleChatSticker({ stickerId, scope }) {
  const [sticker, setSticker] = useState(() => readCachedSticker(stickerId, scope));
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const cached = readCachedSticker(stickerId, scope);
    setSticker(cached);
    setFailed(false);
    // 전송한 콘의 정보는 선택창에서 이미 받았다. 첫 렌더와 서버 확인 후 재생성 모두
    // 이 정보를 바로 써서, 불필요한 개별 조회 때문에 빈 메시지로 기다리지 않게 한다.
    if (cached) return undefined;
    // 조회도 그 채팅 기준이어야 한다. scope 를 빼면 서버가 관제로 보므로, 중계 전용으로 돌려 둔
    // 콘은 보낸 뒤 자기 말풍선에서 404 가 나 '사용할 수 없는 콘입니다' 로 보인다.
    loadStickers(`/v1/stickers/${encodeURIComponent(stickerId)}?scope=${encodeURIComponent(scope)}`).then((result) => {
      if (!cancelled) setSticker(result.sticker);
    }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [stickerId, scope]);
  return <div className="doro-sticker-message" data-sticker-id={stickerId} data-sticker-state={sticker ? 'media' : failed ? 'error' : 'lookup'}>{sticker ? <StickerMedia key={sticker.id} sticker={sticker} />
    : <span>{failed ? '사용할 수 없는 콘입니다.' : '콘 불러오는 중…'}</span>}</div>;
}

// 고르개 칸은 화면 안과 한 화면 아래까지만 그림을 받는다. 브라우저의 loading="lazy" 는 '화면
// 근처' 를 1,000px 넘게 잡아서, 고르개를 여는 순간 팩의 120장을 전부 받았다(2026-10-20 실측).
// 탭을 여러 개 눌러 보면 그 팩들 그림이 모두 줄을 서서, 마지막으로 연 팩이 맨 뒤에서 기다렸다.
// 한 번 받은 칸은 그대로 둔다 - 다시 스크롤할 때 또 받지 않게.
function useNearGrid() {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || near) return undefined;
    if (typeof IntersectionObserver !== 'function') { setNear(true); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { setNear(true); observer.disconnect(); }
    }, { root: node.closest('.doro-sticker-grid-shell'), rootMargin: '0px 0px 100% 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [near]);
  return [ref, near];
}

function StickerChoice({
  sticker,
  favorite,
  favoritePending,
  sendDisabled,
  onSend,
  onToggleFavorite,
}) {
  const [cardRef, near] = useNearGrid();
  return <div ref={cardRef} className={favorite ? 'doro-sticker-card favorite' : 'doro-sticker-card'}>
    <button
      type="button"
      className="doro-sticker-send"
      disabled={sendDisabled}
      aria-label={`콘 ${sticker.dataId} 보내기`}
      onClick={() => onSend(sticker.id)}
    >
      {near ? <StickerMedia sticker={sticker} preview /> : null}
    </button>
    <button
      type="button"
      className={favorite ? 'doro-sticker-favorite active' : 'doro-sticker-favorite'}
      disabled={favoritePending}
      aria-label={favorite ? `콘 ${sticker.dataId} 즐겨찾기 해제` : `콘 ${sticker.dataId} 즐겨찾기 추가`}
      aria-pressed={favorite}
      title={favorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
      onClick={() => onToggleFavorite(sticker)}
    >
      <span aria-hidden="true">{favorite ? '★' : '☆'}</span>
    </button>
  </div>;
}

export function ChatStickerPicker({ room, onOpen, scope = 'control' }) {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef(null);
  const triggerRef = useRef(null);
  const tabsRef = useRef(null);
  const panelId = useId();
  const [mode, setMode] = useState('pack');
  const [packs, setPacks] = useState([]);
  const [packId, setPackId] = useState('');
  const [stickers, setStickers] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(() => new Set(readFavoriteIds()));
  const [favoriteStickers, setFavoriteStickers] = useState([]);
  const [favoritePendingIds, setFavoritePendingIds] = useState(() => new Set());
  const [error, setError] = useState('');
  const [favoriteError, setFavoriteError] = useState('');
  const [loading, setLoading] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [pairMode, setPairMode] = useState(false);
  const [selectedStickers, setSelectedStickers] = useState([]);
  const sendingRef = useRef(false);

  const visibleStickers = useMemo(
    () => mode === 'favorites' ? favoriteStickers : stickers,
    [favoriteStickers, mode, stickers],
  );

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event) => {
      if (!pickerRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    setLoading(true);
    setError('');
    // The pack list and its 177 rows are a 27KB no-store response and a D1 read each. Reopening the
    // picker re-fetched both every time; the admin board calls invalidateStickerCache after an edit,
    // so a short in-memory TTL is enough to keep it fresh.
    loadStickers(`/v1/sticker-packs?scope=${encodeURIComponent(scope)}`, { ttlMs: STICKER_LIST_TTL_MS }).then((result) => {
      if (cancelled) return;
      setPacks(result.packs);
      setPackId((current) => result.packs.some((pack) => pack.id === current) ? current : result.packs[0]?.id || '');
    }).catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    setFavoriteIds(new Set(readFavoriteIds()));
    setFavoriteLoading(true);
    setFavoriteError('');
    loadStickerFavorites(scope).then((result) => {
      if (cancelled) return;
      const nextStickers = Array.isArray(result.stickers) ? result.stickers : [];
      setFavoriteStickers(nextStickers);
      if (result.unavailable) setFavoriteError('저장된 즐겨찾기는 유지됩니다. 일부 콘 정보를 불러오지 못했습니다. 잠시 후 다시 열어주세요.');
      setFavoriteIds(new Set(Array.isArray(result.stickerIds) ? result.stickerIds : nextStickers.map((sticker) => sticker.id)));
    }).catch(() => {
      if (!cancelled) setFavoriteError('즐겨찾기를 불러오지 못했습니다.');
    }).finally(() => {
      if (!cancelled) setFavoriteLoading(false);
    });

    return () => { cancelled = true; };
  }, [open, scope]);

  useEffect(() => {
    if (!open || mode !== 'pack') return undefined;
    let cancelled = false;
    setStickers([]);
    setError('');
    if (!packId) return undefined;
    // 팩 안의 콘도 이 채팅에서 쓰기로 한 것만 받는다. 목록만 거르고 여기를 빼면 팩을 여는 순간
    // 다른 방 콘까지 전부 보인다.
    loadStickers(`/v1/sticker-packs/${encodeURIComponent(packId)}?scope=${encodeURIComponent(scope)}`, { ttlMs: STICKER_LIST_TTL_MS }).then((result) => {
      if (!cancelled) setStickers(result.stickers);
    }).catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [open, mode, packId, scope]);

  async function send(stickerId, stickerIds = null) {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setError('');
    try {
      // 선택창을 오래 열어 캐시 TTL이 지났어도, 방금 고른 정보를 전송 시점에 보존한다.
      for (const id of stickerIds || [stickerId]) {
        const descriptor = selectedStickers.find(item => item.id === id) || visibleStickers.find(item => item.id === id);
        if (descriptor) rememberStickerForChat(descriptor, scope);
      }
      if (await room.sendMessage({ stickerId, stickerIds })) {
        setOpen(false);
        setSelectedStickers([]);
      }
      else setError('콘을 보내지 못했습니다. 다시 시도해주세요.');
    } catch {
      setError('콘을 보내지 못했습니다. 다시 시도해주세요.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  function chooseSticker(stickerId) {
    if (!pairMode) { void send(stickerId); return; }
    const sticker = visibleStickers.find(item => item.id === stickerId);
    if (sticker) setSelectedStickers(current => current.length < 2 ? [...current, sticker] : current);
  }

  async function toggleFavorite(sticker) {
    if (!sticker?.id || favoritePendingIds.has(sticker.id)) return;
    const wasFavorite = favoriteIds.has(sticker.id);
    const nextFavorite = !wasFavorite;

    setFavoriteError('');
    setFavoritePendingIds((current) => new Set(current).add(sticker.id));
    setFavoriteIds((current) => {
      const next = new Set(current);
      if (nextFavorite) next.add(sticker.id);
      else next.delete(sticker.id);
      return next;
    });
    setFavoriteStickers((current) => nextFavorite
      ? [sticker, ...current.filter((item) => item.id !== sticker.id)]
      : current.filter((item) => item.id !== sticker.id));

    try {
      await setStickerFavorite(sticker.id, nextFavorite);
    } catch (err) {
      setFavoriteIds((current) => {
        const next = new Set(current);
        if (wasFavorite) next.add(sticker.id);
        else next.delete(sticker.id);
        return next;
      });
      setFavoriteStickers((current) => wasFavorite
        ? [sticker, ...current.filter((item) => item.id !== sticker.id)]
        : current.filter((item) => item.id !== sticker.id));
      setFavoriteError(err?.message?.startsWith('즐겨찾기는 최대')
        ? err.message : '이 브라우저에 즐겨찾기를 저장하지 못했습니다. 저장 공간과 설정을 확인해주세요.');
    } finally {
      setFavoritePendingIds((current) => {
        const next = new Set(current);
        next.delete(sticker.id);
        return next;
      });
    }
  }

  // 관제 채팅은 시즌이 정해져야 보낼 수 있어서 room.context 를 준다. 중계방은 시즌이 없어서
  // 주지 않는다 - 그대로 읽으면 중계방 화면 전체가 경계로 떨어진다.
  const sendDisabled = sending || Boolean(room?.working)
    || (room?.context ? !room.context.season?.id : false);

  // 콘팩이 많으면 탭을 한 줄로 두고 옆으로 넘긴다. 줄바꿈하면 탭이 격자 자리를 먹는다.
  // PC 마우스 휠은 세로로만 굴러서, 탭 위에서는 가로로 바꿔 준다. React 의 onWheel 은
  // passive 라 기본 스크롤을 못 막으므로 직접 붙인다.
  useEffect(() => {
    const node = tabsRef.current;
    if (!open || !node) return undefined;
    const wheel = (event) => {
      if (node.scrollWidth <= node.clientWidth || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      node.scrollLeft += event.deltaY;
    };
    // 마우스로 잡고 밀어서 넘긴다(2026-10-19: 휠만 되고 끌기가 안 돼서, 잘린 이름을 눌러야만
    // 넘어갔다). 터치는 브라우저가 알아서 밀어 준다. 4px 넘게 끌었으면 놓을 때의 클릭은 버린다 -
    // 안 그러면 밀다가 놓은 자리의 팩이 골라진다.
    let drag = null;
    let dragged = false;
    const down = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || node.scrollWidth <= node.clientWidth) return;
      drag = { x: event.clientX, left: node.scrollLeft };
      dragged = false;
    };
    const move = (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.x;
      if (!dragged && Math.abs(dx) < 4) return;
      dragged = true;
      node.dataset.dragging = 'true';
      node.scrollLeft = drag.left - dx;
    };
    const up = () => {
      drag = null;
      delete node.dataset.dragging;
    };
    const click = (event) => {
      if (!dragged) return;
      dragged = false;
      event.preventDefault();
      event.stopPropagation();
    };
    node.addEventListener('wheel', wheel, { passive: false });
    node.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    node.addEventListener('click', click, true);
    return () => {
      node.removeEventListener('wheel', wheel);
      node.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      node.removeEventListener('click', click, true);
    };
  }, [open]);

  // 고른 팩이 가려져 있으면 보이는 곳까지 넘긴다(다시 열었을 때 고른 팩이 끝에 있을 수 있다).
  useEffect(() => {
    const active = open ? tabsRef.current?.querySelector('[aria-selected="true"]') : null;
    if (!active) return;
    const node = tabsRef.current;
    if (active.offsetLeft < node.scrollLeft || active.offsetLeft + active.offsetWidth > node.scrollLeft + node.clientWidth) {
      node.scrollLeft = active.offsetLeft - (node.clientWidth - active.offsetWidth) / 2;
    }
  }, [open, mode, packId, packs.length]);

  return <div className="doro-sticker-picker" ref={pickerRef}>
    <button type="button" className="doro-sticker-trigger" ref={triggerRef} aria-label="콘 선택"
      title="콘 선택" aria-expanded={open} aria-controls={panelId}
      onPointerDown={(event) => { if (event.cancelable) event.preventDefault(); }}
      onClick={() => { if (!open) { onOpen?.(); setSelectedStickers([]); } setOpen((current) => !current); }}>
      <DororaidIcon name="smile" size={22} />
    </button>
    {open ? <section id={panelId} className="doro-sticker-panel" aria-label="콘 선택창" aria-busy={loading || favoriteLoading}>
      {/* 팩 이름은 조작 버튼과 줄을 분리하고, 많으면 한 줄로 옆으로 넘긴다. */}
      <div className="doro-sticker-bar">
        <div className="doro-sticker-tabs" role="tablist" aria-label="콘 보기 방식" ref={tabsRef}>
          <button type="button" role="tab" aria-selected={mode === 'favorites'} className={mode === 'favorites' ? 'active' : ''}
            onClick={() => setMode('favorites')}><span aria-hidden="true">★</span> 즐겨찾기 <b>{favoriteIds.size}</b></button>
          {packs.map((pack) => <button key={pack.id} type="button" role="tab"
            aria-selected={mode === 'pack' && packId === pack.id}
            className={mode === 'pack' && packId === pack.id ? 'active' : ''}
            onClick={() => { setMode('pack'); setPackId(pack.id); }}>{pack.name}</button>)}
        </div>
        <button type="button" className="doro-sticker-pair-toggle" aria-pressed={pairMode} disabled={sending}
          onClick={() => { setPairMode(current => !current); setSelectedStickers([]); }}>더블콘</button>
        <button type="button" className="doro-sticker-close" aria-label="콘 선택창 닫기" onClick={() => setOpen(false)}><DororaidIcon name="close" size={18} /></button>
      </div>

      {pairMode ? <div className="doro-sticker-pair-selection">
        <div>{[0, 1].map(index => <button key={index} type="button" disabled={sending || !selectedStickers[index]}
          aria-label={`${index + 1}번째 콘 선택 취소`} onClick={() => setSelectedStickers(current => current.filter((_, i) => i !== index))}>
          {selectedStickers[index] ? <StickerMedia sticker={selectedStickers[index]} preview /> : <span>{index + 1}</span>}
        </button>)}</div>
        <button type="button" disabled={sendDisabled || selectedStickers.length !== 2}
          onClick={() => void send(selectedStickers[0].id, selectedStickers.map(item => item.id))}>더블콘 보내기</button>
      </div> : null}

      {mode === 'pack' && loading ? <p role="status">콘팩 불러오는 중…</p> : null}
      {mode === 'pack' && !loading && !packs.length && !error ? <p>등록된 콘팩이 없습니다.</p> : null}

      {mode === 'favorites' && favoriteLoading ? <p role="status">즐겨찾기 불러오는 중…</p> : null}
      {mode === 'favorites' && !favoriteLoading && !favoriteStickers.length && !favoriteError
        ? <p className="doro-sticker-empty">{favoriteIds.size ? '이 채팅에서 사용할 수 있는 즐겨찾기 콘이 없습니다.' : '즐겨찾기한 콘이 없습니다. ☆ 버튼으로 추가할 수 있습니다.'}</p>
        : null}

      <div className="doro-sticker-grid-shell">
        <div className="doro-sticker-grid">
        {visibleStickers.map((sticker) => <StickerChoice
          key={sticker.id}
          sticker={sticker}
          favorite={favoriteIds.has(sticker.id)}
          favoritePending={favoriteLoading || favoritePendingIds.has(sticker.id)}
          sendDisabled={sendDisabled || (pairMode && selectedStickers.length === 2)}
          onSend={chooseSticker}
          onToggleFavorite={toggleFavorite}
        />)}
        </div>
      </div>

      {favoriteError ? <p className="doro-sticker-favorite-error" role="alert">{favoriteError}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
    </section> : null}
  </div>;
}
